/**
 * RouteOnMap component displays driving directions on a Google Map.
 *
 * It re-renders conditionally based on the `shouldPreserveViewport` prop to preserve
 * the current map viewport when needed.
*
 * @param {Object} directions - Directions data object returned from the Google Maps API.
 * @param {boolean} shouldPreserveViewport - Focusing on the route when not in search mode or when in zoom mode or when initial load.
 *
 * @description
 * Renders a `DirectionsRenderer` component with the following options:
 * - `preserveViewport`: Keeps the current map view without auto-zooming to fit the route.
 * - `suppressMarkers`: Hides the default start and end markers.
 *
 * @returns {JSX.Element} A configured `DirectionsRenderer` component.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DirectionsRenderer } from '@react-google-maps/api';

const DirectionsRendererWrapper = ({ directions, shouldPreserveViewport }) => {
  const [options, setOptions] = useState({
    preserveViewport: shouldPreserveViewport,
    suppressMarkers: true,
  });
  useEffect(() => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      preserveViewport: shouldPreserveViewport,
    }));
  }, [shouldPreserveViewport]);
  return (
    <DirectionsRenderer
      directions={directions}
      options={options}
    />
  );
};

DirectionsRendererWrapper.propTypes = {
  directions: PropTypes.object.isRequired,
  shouldPreserveViewport: PropTypes.bool.isRequired,
};

export default DirectionsRendererWrapper;
